import {
  Grid,
  Column,
  Heading,
  Button,
  TextInput,
  FormGroup,
  InlineNotification,
  Tile,
  Content,
} from '@carbon/react';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { type SEOHandle } from '@nasa-gcn/remix-seo';
import * as E from '@react-email/components';
import { data, redirect, Link, useFetcher } from 'react-router';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx';
import { prisma } from '#app/utils/db.server.ts';
import { sendEmail } from '#app/utils/email.server.ts';
import { checkHoneypot } from '#app/utils/honeypot.server.ts';
import { getSystemData } from '#app/utils/shared-auth.server.ts';
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts';
import { type Route } from './+types/forgot-password.ts';
import { prepareVerification } from './verify.server.ts';

export const handle: SEOHandle = {
  getSitemapEntries: () => null,
};

const ForgotPasswordSchema = z.object({
  usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
});

export async function loader(): Promise<{ systemName: string }> {
  const { systemName } = await getSystemData();
  return { systemName };
}

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  await checkHoneypot(formData);
  const submission = await parseWithZod(formData, {
    schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
      const user = await prisma.user.findFirst({
        where: {
          OR: [
            { email: data.usernameOrEmail },
            { username: data.usernameOrEmail },
          ],
        },
        select: { id: true },
      });
      if (!user) {
        ctx.addIssue({
          path: ['usernameOrEmail'],
          code: z.ZodIssueCode.custom,
          message: 'No user exists with this username or email',
        });
        return;
      }
    }),
    async: true,
  });
  if (submission.status !== 'success') {
    return data(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    );
  }
  const { usernameOrEmail } = submission.value;

  const user = await prisma.user.findFirstOrThrow({
    where: { OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }] },
    select: { email: true, username: true },
  });

  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: 'reset-password',
    target: usernameOrEmail,
  });

  const { systemName } = await getSystemData();
  const response = await sendEmail({
    to: user.email,
    subject: `${systemName} Password Reset`,
    react: (
      <ForgotPasswordEmail
        onboardingUrl={verifyUrl.toString()}
        otp={otp}
        systemName={systemName}
      />
    ),
  });

  if (response.status === 'success') {
    return redirect(redirectTo.toString());
  } else {
    return data(
      { result: submission.reply({ formErrors: [response.error.message] }) },
      { status: 500 },
    );
  }
}

function ForgotPasswordEmail({
  onboardingUrl,
  otp,
  systemName,
}: {
  onboardingUrl: string;
  otp: string;
  systemName: string;
}) {
  return (
    <E.Html lang="en" dir="ltr">
      <E.Container>
        <h1>
          <E.Text>{systemName} Password Reset</E.Text>
        </h1>
        <p>
          <E.Text>
            Here's your verification code: <strong>{otp}</strong>
          </E.Text>
        </p>
        <p>
          <E.Text>Or click the link:</E.Text>
        </p>
        <E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
      </E.Container>
    </E.Html>
  );
}

export default function ForgotPasswordRoute({
  loaderData,
}: Route.ComponentProps) {
  const forgotPassword = useFetcher<typeof action>();

  const [form, fields] = useForm({
    id: 'forgot-password-form',
    constraint: getZodConstraint(ForgotPasswordSchema),
    lastResult: forgotPassword.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema });
    },
    shouldRevalidate: 'onBlur',
  });

  return (
    <>
      <title>{`Password Recovery for ${loaderData.systemName}`}</title>
      <Content className="min-h-screen bg-gray-50 dark:bg-gray-900 mt-0!">
        <Grid className="">
          <Column sm={4} md={8} lg={16} className="mx-0!">
            <div className="flex min-h-screenx justify-center items-centerx mt-52">
              <Tile className="p-8 max-w-md w-full">
                <div className="flex items-center justify-center gap-2 mb-6">
                  {/* <img
                    src="/img/cb-logo-black.png"
                    alt="CircuitBread Logo"
                    className="h-8 w-auto"
                  /> */}
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    {loaderData.systemName}
                  </span>
                </div>

                <div className="text-center mb-6">
                  <Heading className="mb-4">Forgot Password</Heading>
                  <p className="text-gray-600 dark:text-gray-400">
                    No worries, we'll send you reset instructions.
                  </p>
                </div>

                <forgotPassword.Form method="POST" {...getFormProps(form)}>
                  <HoneypotInputs />

                  {form.errors && form.errors.length > 0 && (
                    <div className="mb-4">
                      <InlineNotification
                        kind="error"
                        title="Error"
                        subtitle={form.errors.join(', ')}
                        hideCloseButton
                      />
                    </div>
                  )}

                  <FormGroup legendText="" className="mb-4!">
                    <TextInput
                      labelText="Username or Email"
                      placeholder="Enter your username or email"
                      autoFocus
                      invalid={!!fields.usernameOrEmail.errors?.length}
                      invalidText={fields.usernameOrEmail.errors?.[0]}
                      {...getInputProps(fields.usernameOrEmail, {
                        type: 'text',
                      })}
                    />
                  </FormGroup>

                  <Button
                    type="submit"
                    kind="primary"
                    size="lg"
                    disabled={forgotPassword.state !== 'idle'}
                    className="disabled:text-[#4E5A66] hover:disabled:text-[#4E5A66] mt-6"
                  >
                    {forgotPassword.state === 'submitting'
                      ? 'Sending...'
                      : 'Recover password'}
                  </Button>
                </forgotPassword.Form>

                <div className="text-center mt-6">
                  <Link
                    to="/login"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 underline underline-offset-4"
                  >
                    Back to Login
                  </Link>
                </div>
              </Tile>
            </div>
          </Column>
        </Grid>
      </Content>
    </>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
